import type {
  AnalyticsActivityData,
  AnalyticsActivityKind,
  AnalyticsConversationMessage,
  AnalyticsTurnData,
} from "@/api/rest/services/analyticsService";

export interface AnalyticsActivityEntry {
  id: string;
  kind: AnalyticsActivityKind;
  text: string;
  status?: string | null;
}

export interface AnalyticsAssistantTurn {
  role: "assistant";
  turnId?: string;
  status?: "cancelled";
  finalAnswer: string;
  activityEntries: AnalyticsActivityEntry[];
  startedAt: number;
  completedAt?: number;
  error?: string;
}

export interface AnalyticsUserTurn {
  role: "user";
  content: string;
}

export type AnalyticsConversationItem = AnalyticsUserTurn | AnalyticsAssistantTurn;

export function createAssistantTurn(startedAt: number = Date.now()): AnalyticsAssistantTurn {
  return {
    role: "assistant",
    finalAnswer: "",
    activityEntries: [],
    startedAt,
  };
}

export function applyActivityToTurn(
  current: AnalyticsAssistantTurn | null,
  incoming: AnalyticsActivityData,
  startedAt: number = Date.now(),
): AnalyticsAssistantTurn {
  const next = current ?? createAssistantTurn(startedAt);

  return {
    ...next,
    activityEntries: upsertActivityEntry(next.activityEntries, incoming),
  };
}

export function applyMessageToTurn(
  current: AnalyticsAssistantTurn | null,
  content: string,
  startedAt: number = Date.now(),
): AnalyticsAssistantTurn {
  const next = current ?? createAssistantTurn(startedAt);

  return {
    ...next,
    finalAnswer: content,
    error: undefined,
  };
}

export function finalizeAssistantTurn(
  current: AnalyticsAssistantTurn | null,
  updater?: (current: AnalyticsAssistantTurn) => AnalyticsAssistantTurn,
  completedAt: number = Date.now(),
): AnalyticsAssistantTurn | null {
  if (!current) {
    return null;
  }

  const baseTurn: AnalyticsAssistantTurn = {
    ...current,
    completedAt: current.completedAt ?? completedAt,
  };

  return updater ? updater(baseTurn) : baseTurn;
}

// English only, like the rest of the analytics page. The server still finishes the turn; a reload shows it.
export const STREAM_LOST_ERROR = "Connection lost. Reload to see the answer.";

/** Ends a turn whose stream closed without a done or error event. Null when there is nothing to end. */
export function finalizeIfUnterminated(
  current: AnalyticsAssistantTurn | null,
  sawTerminalEvent: boolean,
  completedAt: number = Date.now(),
): AnalyticsAssistantTurn | null {
  if (sawTerminalEvent) {
    return null;
  }
  return finalizeAssistantTurn(current, (turn) => ({ ...turn, error: STREAM_LOST_ERROR }), completedAt);
}

export function hydrateAnalyticsConversation(
  messages: AnalyticsConversationMessage[],
  timestamp: number = Date.now(),
): AnalyticsConversationItem[] {
  return messages.map((message, index) => {
    if (message.role === "user") {
      return {
        role: "user",
        content: message.content,
      };
    }

    return {
      role: "assistant",
      finalAnswer: message.content,
      activityEntries: [],
      startedAt: timestamp + index,
      completedAt: timestamp + index,
    };
  });
}

export function createAssistantTurnFromSnapshot(snapshot: AnalyticsTurnData): AnalyticsAssistantTurn {
  const isCancelled = snapshot.status === "cancelled";
  return {
    role: "assistant",
    turnId: snapshot.turnId,
    status: isCancelled ? "cancelled" : undefined,
    finalAnswer: snapshot.finalAnswer ?? "",
    activityEntries: snapshot.activities.map((activity) => ({
      id: activity.id,
      kind: activity.kind,
      text: activity.text,
      status: activity.status,
    })),
    startedAt: parseIsoTimestamp(snapshot.startedAt) ?? Date.now(),
    completedAt:
      snapshot.completedAt || snapshot.status === "completed" || snapshot.status === "failed" || isCancelled
        ? (parseIsoTimestamp(snapshot.completedAt) ?? Date.now())
        : undefined,
    error: !isCancelled && snapshot.error ? `Error: ${snapshot.error}` : undefined,
  };
}

export function applySnapshotToTurn(
  current: AnalyticsAssistantTurn | null,
  snapshot: AnalyticsTurnData,
): AnalyticsAssistantTurn {
  const next = createAssistantTurnFromSnapshot(snapshot);

  return {
    ...next,
    startedAt: next.startedAt || current?.startedAt || Date.now(),
  };
}

function upsertActivityEntry(
  entries: AnalyticsActivityEntry[],
  incoming: AnalyticsActivityData,
): AnalyticsActivityEntry[] {
  const nextEntry: AnalyticsActivityEntry = {
    id: incoming.id,
    kind: incoming.kind,
    text: incoming.text,
    status: incoming.status,
  };
  const existingIndex = entries.findIndex((entry) => entry.id === incoming.id);

  if (existingIndex === -1) {
    return [...entries, nextEntry];
  }

  const nextEntries = [...entries];
  nextEntries[existingIndex] = nextEntry;
  return nextEntries;
}

function parseIsoTimestamp(value?: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? undefined : timestamp;
}
